import { useState, useEffect } from 'react';
import { categories } from '../../mocks/categories';

const CUSTOM_VALUE = '__custom__';

// A category picker that also accepts a free-typed value: choose one of the
// platform's existing categories from the dropdown, or pick "Other" to type
// a category of your own. The underlying value is always just a plain
// string -- the Course model's `category` field has no fixed enum -- so a
// custom category behaves exactly like a built-in one everywhere it's used.
//
// Shared by every place an instructor picks a course category while
// creating/editing a course (the manual course form, the AI course
// generator, ...) so the behavior stays identical across all of them.
export default function CategorySelect({ value, onChange, className = '' }) {
  const knownIds = categories.map((c) => c.id);
  const isKnown = knownIds.includes(value);
  const [customMode, setCustomMode] = useState(Boolean(value) && !isKnown);

  // Keep custom-mode in sync when `value` changes from outside this
  // component -- an existing course loading in, or AI filling in a
  // category -- without fighting the moment the user picks "Other" below
  // (which deliberately clears the value while staying in custom mode).
  useEffect(() => {
    if (value) {
      setCustomMode(!knownIds.includes(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val === CUSTOM_VALUE) {
      setCustomMode(true);
      onChange('');
    } else {
      setCustomMode(false);
      onChange(val);
    }
  };

  return (
    <div className={className}>
      <select
        value={customMode ? CUSTOM_VALUE : (value || '')}
        onChange={handleSelectChange}
        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
      >
        <option value="" disabled>Select a category</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>{cat.name}</option>
        ))}
        <option value={CUSTOM_VALUE}>Other (type your own)...</option>
      </select>

      {customMode && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter a custom category"
          autoFocus
          className="w-full mt-2 px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
        />
      )}
    </div>
  );
}
